import { gsap } from "gsap";
import "./styles.css";

type ViewId =
  | "overview"
  | "projects"
  | "documents"
  | "valuation"
  | "judicial"
  | "tax"
  | "rent"
  | "workflow"
  | "reports";

type NavItem = {
  id: ViewId;
  label: string;
  icon: string;
};

const icons: Record<string, string> = {
  overview: '<svg viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="4" rx="1"/><rect x="14" y="11" width="7" height="10" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/></svg>',
  projects: '<svg viewBox="0 0 24 24"><path d="M3.5 7.5h17v12h-17z"/><path d="M8 7.5V5h8v2.5M3.5 12h17"/></svg>',
  documents: '<svg viewBox="0 0 24 24"><path d="M5 3.5h9l5 5v12H5z"/><path d="M14 3.5v5h5M8 13h8M8 17h6"/></svg>',
  valuation: '<svg viewBox="0 0 24 24"><path d="M4 19.5V11M10 19.5V5M16 19.5v-7M22 19.5V8"/><path d="M2 19.5h21"/></svg>',
  judicial: '<svg viewBox="0 0 24 24"><path d="M12 3l7 3v5c0 4.8-2.8 8.2-7 10-4.2-1.8-7-5.2-7-10V6z"/><path d="M8.5 12l2.2 2.2 4.8-5"/></svg>',
  tax: '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M8 9h8M8 15h8M12 6v12"/></svg>',
  rent: '<svg viewBox="0 0 24 24"><path d="M12 21s7-5.1 7-11a7 7 0 10-14 0c0 5.9 7 11 7 11z"/><circle cx="12" cy="10" r="2.3"/></svg>',
  workflow: '<svg viewBox="0 0 24 24"><path d="M4 6h11M4 12h16M4 18h9"/><circle cx="18" cy="6" r="2"/><circle cx="16" cy="18" r="2"/></svg>',
  reports: '<svg viewBox="0 0 24 24"><path d="M5 3.5h14v17H5z"/><path d="M8 8h8M8 12h8M8 16h5"/></svg>',
  spark: '<svg viewBox="0 0 24 24"><path d="M12 3l1.5 5.2L19 10l-5.5 1.8L12 17l-1.5-5.2L5 10l5.5-1.8z"/><path d="M19 16l.6 2.1L22 19l-2.4.9L19 22l-.6-2.1L16 19l2.4-.9z"/></svg>',
  plus: '<svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg>',
  search: '<svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="6.5"/><path d="M16 16l4 4"/></svg>',
  bell: '<svg viewBox="0 0 24 24"><path d="M6 17h12l-1.4-2V10a4.6 4.6 0 00-9.2 0v5zM10 20h4"/></svg>',
  menu: '<svg viewBox="0 0 24 24"><path d="M4 7h16M4 12h16M4 17h16"/></svg>',
  close: '<svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18"/></svg>',
  upload: '<svg viewBox="0 0 24 24"><path d="M12 16V4M7.5 8.5L12 4l4.5 4.5M5 14v5h14v-5"/></svg>',
  export: '<svg viewBox="0 0 24 24"><path d="M12 4v12M7.5 11.5L12 16l4.5-4.5M5 20h14"/></svg>',
  check: '<svg viewBox="0 0 24 24"><path d="M5 12.5l4 4L19 7"/></svg>',
};

const navItems: NavItem[] = [
  { id: "overview", label: "决策总览", icon: "overview" },
  { id: "projects", label: "项目库", icon: "projects" },
  { id: "documents", label: "AI 尽调", icon: "documents" },
  { id: "valuation", label: "投资测算", icon: "valuation" },
  { id: "judicial", label: "司法风控", icon: "judicial" },
  { id: "tax", label: "税务规则", icon: "tax" },
  { id: "rent", label: "租金案例", icon: "rent" },
  { id: "workflow", label: "投融管退", icon: "workflow" },
  { id: "reports", label: "报告中心", icon: "reports" },
];

function requireElement<T extends Element>(selector: string) {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Required element was not found: ${selector}`);
  return element;
}

const app = requireElement<HTMLDivElement>("#app");

const icon = (name: string, className = "") =>
  (icons[name] ?? icons.overview).replace("<svg", `<svg class="${className}" aria-hidden="true"`);

const navMarkup = navItems
  .map(
    (item) => `
      <button class="nav-item ${item.id === "overview" ? "active" : ""}" data-nav="${item.id}" aria-label="${item.label}">
        ${icon(item.icon, "nav-icon")}
        <span>${item.label}</span>
      </button>`,
  )
  .join("");

app.innerHTML = `
  <div class="app-shell">
    <aside class="sidebar" id="sidebar">
      <div class="brand">
        <div class="brand-mark">特</div>
        <div class="brand-copy">
          <div class="brand-name">特资智投系统</div>
          <div class="brand-sub">星河数科</div>
        </div>
      </div>
      <div class="nav-label">工作台</div>
      <nav class="nav-list" aria-label="主要功能">${navMarkup}</nav>
      <div class="sidebar-bottom">
        <div class="demo-note">
          会议演示环境<br />
          项目、案件、税率及测算结果均为模拟数据。
        </div>
        <div class="user-chip">
          <div class="avatar">况</div>
          <div class="user-copy"><strong>况先生</strong><small>投资负责人</small></div>
        </div>
      </div>
    </aside>

    <section class="workspace">
      <header class="topbar">
        <div class="project-switcher">
          <button class="icon-button mobile-menu" id="menu-button" aria-label="打开菜单">${icon("menu")}</button>
          <span class="project-dot"></span>
          <div><small>当前项目 · PRJ-2026-001</small><strong>成都核心商铺债权收购项目</strong></div>
        </div>
        <div class="top-actions">
          <button class="icon-button" id="search-button" aria-label="智能搜索">${icon("search")}</button>
          <button class="icon-button" id="notice-button" aria-label="查看通知">${icon("bell")}</button>
          <button class="button" data-nav="reports">${icon("export")} 导出项目快照</button>
        </div>
      </header>
      <main class="main-content" id="view-root"></main>
    </section>
  </div>

  <button class="assistant-trigger" id="assistant-trigger" aria-expanded="false">
    <span class="spark">${icon("spark")}</span><span>问智投助手</span>
  </button>
  <aside class="assistant-panel" id="assistant-panel" aria-label="智投 AI 助手">
    <div class="assistant-head">
      <div class="assistant-brand"><span class="spark">${icon("spark")}</span><div><strong>智投 AI 助手</strong><small>演示模式 · 证据可追溯</small></div></div>
      <button class="icon-button" id="assistant-close" aria-label="关闭助手">${icon("close")}</button>
    </div>
    <div class="assistant-body" id="assistant-messages">
      <div class="message">我已归集当前项目的 23 份材料和 48 项结构化数据。您想先看风险、测算还是投委材料？<span class="message-source">来源：项目档案 v0.8 · 人工复核 76%</span></div>
      <div class="suggestion-list">
        <button class="suggestion">最大的三个风险是什么？</button>
        <button class="suggestion">为什么最高买价是 3,135 万？</button>
        <button class="suggestion">整理投委会摘要</button>
      </div>
    </div>
    <form class="assistant-input" id="assistant-form">
      <input id="assistant-query" autocomplete="off" placeholder="输入您的问题…" aria-label="向助手提问" />
      <button class="button primary" type="submit">发送</button>
    </form>
  </aside>

  <div class="modal-backdrop" id="modal-backdrop" role="dialog" aria-modal="true" aria-hidden="true">
    <div class="modal" id="modal"></div>
  </div>
  <div class="toast-stack" id="toast-stack" aria-live="polite"></div>
  <nav class="mobile-nav" aria-label="移动快捷导航">
    <button class="active" data-nav="overview">${icon("overview")}<span>总览</span></button>
    <button data-nav="documents">${icon("documents")}<span>AI尽调</span></button>
    <button data-nav="valuation">${icon("valuation")}<span>测算</span></button>
    <button data-nav="workflow">${icon("workflow")}<span>进度</span></button>
  </nav>
`;

const viewRoot = requireElement<HTMLElement>("#view-root");
const sidebar = requireElement<HTMLElement>("#sidebar");
const assistantPanel = requireElement<HTMLElement>("#assistant-panel");
const assistantTrigger = requireElement<HTMLButtonElement>("#assistant-trigger");
const modalBackdrop = requireElement<HTMLElement>("#modal-backdrop");
const modal = requireElement<HTMLElement>("#modal");
const toastStack = requireElement<HTMLElement>("#toast-stack");

let viewContext: gsap.Context | null = null;
const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const pageHeading = (eyebrow: string, title: string, description: string, actions = "") => `
  <div class="page-heading reveal-item">
    <div><div class="eyebrow">${eyebrow}</div><h1>${title}</h1><p>${description}</p></div>
    ${actions ? `<div class="heading-actions">${actions}</div>` : ""}
  </div>`;

const overviewView = () => `
  <section class="view" data-view="overview">
    <div class="hero-dashboard reveal-item">
      <div class="hero-copy">
        <div class="eyebrow">Evidence-led Investment Intelligence</div>
        <h1>从每一份证据，<br />到每一个<span>决策</span></h1>
        <p>将分散的债权、物权、司法、税务与市场数据，沉淀为可复核的投资结论，贯穿“投、融、管、退”全周期。</p>
        <div class="hero-actions">
          <button class="button primary" data-nav="documents">${icon("spark")} 开始 AI 尽调</button>
          <button class="button" data-nav="valuation">${icon("valuation")} 查看投资测算</button>
        </div>
      </div>
      <div class="decision-orbit" aria-label="项目综合投资评分 82 分">
        <div class="orbit-ring ring-one"></div><div class="orbit-ring ring-two"></div>
        <div class="score-core"><div><b class="counter" data-value="82">0</b><small>综合投资评分</small></div></div>
        <span class="orbit-label l1">法律确定性 76%</span><span class="orbit-label l2">退出可行性 84%</span>
        <span class="orbit-label l3">财务吸引力 88%</span><span class="orbit-label l4">证据完整度 71%</span>
      </div>
    </div>

    <div class="kpi-grid">
      <article class="kpi-card reveal-item"><div class="kpi-label"><span>在管项目规模</span>${icon("projects")}</div><div class="kpi-value">¥ <span class="counter" data-value="3.82" data-decimals="2">0</span> 亿</div><div class="kpi-change">↗ 12 个项目 · 3 个重点推进</div></article>
      <article class="kpi-card reveal-item"><div class="kpi-label"><span>预计项目 IRR</span>${icon("valuation")}</div><div class="kpi-value"><span class="counter" data-value="21.4" data-decimals="1">0</span>%</div><div class="kpi-change">高于目标回报 3.4%</div></article>
      <article class="kpi-card reveal-item"><div class="kpi-label"><span>建议最高买价</span>${icon("tax")}</div><div class="kpi-value">¥ <span class="counter" data-value="3135">0</span> 万</div><div class="kpi-change neutral">安全边际 30.0%</div></article>
      <article class="kpi-card reveal-item"><div class="kpi-label"><span>待复核风险</span>${icon("judicial")}</div><div class="kpi-value"><span class="counter" data-value="7">0</span> 项</div><div class="kpi-change neutral">2 项需要律师确认</div></article>
    </div>

    <div class="dashboard-grid">
      <section class="panel reveal-item">
        <div class="panel-header"><div><h2>重点项目</h2><p>根据投资评分、时间窗口与待办优先级排序</p></div><button class="button small" data-nav="projects">全部项目 ↗</button></div>
        <div class="table-wrap">
          <table class="data-table">
            <thead><tr><th>项目</th><th>阶段</th><th>投资评分</th><th>预计 IRR</th><th>核心风险</th></tr></thead>
            <tbody>
              <tr data-nav="documents"><td><span class="cell-main">成都核心商铺债权项目</span><span class="cell-sub">锦江区 · 商业 9,000㎡</span></td><td><span class="status-pill warn">投资测算</span></td><td class="mono">82</td><td class="mono">21.4%</td><td><span class="tag danger">欠税待核</span></td></tr>
              <tr><td><span class="cell-main">川南工业债权包</span><span class="cell-sub">泸州 · 本金 1,995 万</span></td><td><span class="status-pill info">法律尽调</span></td><td class="mono">74</td><td class="mono">17.2%</td><td><span class="tag warn">周期偏长</span></td></tr>
              <tr><td><span class="cell-main">贵阳住宅抵债资产</span><span class="cell-sub">观山湖 · 住宅 18 套</span></td><td><span class="status-pill good">退出准备</span></td><td class="mono">79</td><td class="mono">12.8%</td><td><span class="tag info">价格波动</span></td></tr>
            </tbody>
          </table>
        </div>
      </section>
      <section class="panel reveal-item">
        <div class="panel-header"><div><h2>当前决策路径</h2><p>从资料归集到投委审批</p></div><span class="status-pill warn">进行中</span></div>
        <div class="workflow-mini">
          <div class="workflow-row"><span class="flow-dot done">✓</span><div><strong>项目入库与资料解析</strong><span>23 份文件已归档</span></div><time>已完成</time></div>
          <div class="workflow-row"><span class="flow-dot done">✓</span><div><strong>法律与财税尽调</strong><span>31/38 项已复核</span></div><time>82%</time></div>
          <div class="workflow-row"><span class="flow-dot now">3</span><div><strong>估值与最高买价测算</strong><span>正在补充租金可比案例</span></div><time>今日</time></div>
          <div class="workflow-row"><span class="flow-dot">4</span><div><strong>投委材料与审批</strong><span>待上会条件确认</span></div><time>08/14</time></div>
        </div>
      </section>
    </div>

    <div class="section-space module-grid">
      ${navItems.slice(2, 8).map((item, index) => `<article class="feature-card reveal-item" data-nav="${item.id}"><span class="feature-number">0${index + 1}</span><div class="feature-icon">${icon(item.icon)}</div><h3>${item.label}</h3><p>${["文档解析、要素抽取、缺失项提示与人工复核。", "市场法、收益法、成本与风险折价的透明测算。", "案件时间轴、权利顺位、风险标签与取证清单。", "分地区、主体、资产类型的规则匹配与证据链。", "附近租金案例地图、可信等级与可比性调整。", "以投、融、管、退四条主线跟踪任务、审批与现金流。"][index]}</p><span class="feature-arrow">↗</span></article>`).join("")}
    </div>
  </section>`;

const projectsView = () => `
  <section class="view" data-view="projects">
    ${pageHeading("Portfolio Intelligence", "项目库", "集中沉淀项目、债权、抵押物与投资结论，让每一个项目的阶段、负责人和风险一目了然。", `<button class="button" id="import-project">${icon("upload")} 批量导入</button><button class="button primary" id="new-project">${icon("plus")} 新建项目</button>`)}
    <div class="filter-bar reveal-item"><button class="filter-chip active">全部 12</button><button class="filter-chip">初筛 3</button><button class="filter-chip">尽调 4</button><button class="filter-chip">测算 2</button><button class="filter-chip">投委 1</button><button class="filter-chip">管理与退出 2</button></div>
    <section class="panel reveal-item">
      <div class="table-wrap"><table class="data-table">
        <thead><tr><th>项目编号 / 名称</th><th>交易类型</th><th>投资规模</th><th>项目阶段</th><th>负责人</th><th>评分</th><th>IRR</th><th>最近更新</th></tr></thead>
        <tbody>
          <tr data-nav="documents"><td><span class="cell-main">PRJ-2026-001 · 成都核心商铺债权</span><span class="cell-sub">四川省成都市锦江区</span></td><td>债权收购</td><td class="mono">¥ 3,135 万</td><td><span class="status-pill warn">投资测算</span></td><td>况先生</td><td class="mono">82</td><td class="mono">21.4%</td><td>5 分钟前</td></tr>
          <tr><td><span class="cell-main">PRJ-2026-002 · 川南工业债权包</span><span class="cell-sub">四川省泸州市龙马潭区</span></td><td>债权转让</td><td class="mono">¥ 1,260 万</td><td><span class="status-pill info">法律尽调</span></td><td>李律师</td><td class="mono">74</td><td class="mono">17.2%</td><td>今日</td></tr>
          <tr><td><span class="cell-main">PRJ-2026-003 · 贵阳住宅抵债资产</span><span class="cell-sub">贵州省贵阳市观山湖区</span></td><td>抵债资产</td><td class="mono">¥ 2,480 万</td><td><span class="status-pill good">退出准备</span></td><td>王经理</td><td class="mono">79</td><td class="mono">12.8%</td><td>昨日</td></tr>
          <tr><td><span class="cell-main">PRJ-2026-004 · 重庆巴南物流园法拍</span><span class="cell-sub">重庆市巴南区</span></td><td>司法拍卖</td><td class="mono">¥ 6,800 万</td><td><span class="status-pill">资料初筛</span></td><td>陈顾问</td><td class="mono">68</td><td class="mono">--</td><td>08/07</td></tr>
          <tr><td><span class="cell-main">PRJ-2026-005 · 成都高新写字楼重整</span><span class="cell-sub">四川省成都市高新区</span></td><td>破产重整</td><td class="mono">¥ 1.2 亿</td><td><span class="status-pill danger">待补资料</span></td><td>周经理</td><td class="mono">61</td><td class="mono">--</td><td>08/05</td></tr>
        </tbody>
      </table></div>
    </section>
    <div class="kpi-grid section-space">
      <article class="kpi-card reveal-item"><div class="kpi-label"><span>当月新入库</span></div><div class="kpi-value">5</div><div class="kpi-change">其中 3 个进入尽调</div></article>
      <article class="kpi-card reveal-item"><div class="kpi-label"><span>累计投资规模</span></div><div class="kpi-value">¥ 3.82 亿</div><div class="kpi-change neutral">12 个在管项目</div></article>
      <article class="kpi-card reveal-item"><div class="kpi-label"><span>平均项目评分</span></div><div class="kpi-value">72.8</div><div class="kpi-change">较上月 +2.4</div></article>
      <article class="kpi-card reveal-item"><div class="kpi-label"><span>资料完整率</span></div><div class="kpi-value">76%</div><div class="kpi-change neutral">18 项待补证</div></article>
    </div>
  </section>`;

const documentsView = () => `
  <section class="view" data-view="documents">
    ${pageHeading("AI Due Diligence", "AI 尽调助手", "上传公告、合同、裁判文书与评估报告，AI 只负责抽取与初步分析，最终结论由专业人员复核生效。", `<button class="button" id="evidence-export">${icon("export")} 导出证据清单</button><button class="button primary" id="analyze-files">${icon("spark")} 重新分析</button>`)}
    <div class="two-column">
      <section class="panel panel-pad reveal-item">
        <div class="drop-zone" id="drop-zone" tabindex="0">${icon("upload")}<div><strong>将项目文件拖放到此处</strong><span>支持 PDF、Word、Excel 与图片 · 单份最大 50MB</span></div></div>
        <div class="file-list section-space" id="file-list">
          <div class="file-item"><span class="file-icon">PDF</span><div><strong>司法拍卖公告.pdf</strong><p>12 页 · 识别 18 个关键字段 · 来源：人民法院诉讼资产网</p></div><span class="status-pill good">已复核</span></div>
          <div class="file-item"><span class="file-icon">DOC</span><div><strong>债权收购协议.docx</strong><p>24 页 · 识别债权本息与担保信息 · 内部文件</p></div><span class="status-pill warn">待复核</span></div>
          <div class="file-item"><span class="file-icon">PDF</span><div><strong>抵押物评估报告.pdf</strong><p>86 页 · OCR 完成 · 附件中 3 处地址表述待核</p></div><span class="status-pill info">系统识别</span></div>
          <div class="file-item"><span class="file-icon">IMG</span><div><strong>不动产登记信息.jpg</strong><p>4 张 · 权证号与抵押权人已抽取</p></div><span class="status-pill good">已复核</span></div>
        </div>
      </section>
      <section class="panel reveal-item">
        <div class="panel-header"><div><h2>结构化抽取结果</h2><p>保留原文定位、模型版本与复核状态</p></div><span class="data-badge">MODEL v0.8</span></div>
        <div class="panel-pad" id="extraction-results">
          <article class="extraction-card"><header><strong>债权要素</strong><span class="status-pill good">96% 可信</span></header><dl><div><dt>债权本金</dt><dd>¥ 2,800 万</dd></div><div><dt>本息合计</dt><dd>¥ 3,476 万</dd></div><div><dt>债务人</dt><dd>成都某商管公司</dd></div><div><dt>时效状态</dt><dd>执行程序中</dd></div></dl></article>
          <article class="extraction-card"><header><strong>抵押物要素</strong><span class="status-pill warn">88% 可信</span></header><dl><div><dt>资产类型</dt><dd>商业综合体</dd></div><div><dt>建筑面积</dt><dd>9,012.6 ㎡</dd></div><div><dt>评估价值</dt><dd>¥ 5,680 万</dd></div><div><dt>抵押顺位</dt><dd>第一顺位</dd></div></dl></article>
          <article class="extraction-card"><header><strong>缺失与冲突</strong><span class="status-pill danger">3 项待处理</span></header><dl><div><dt>历史欠税</dt><dd>未取得书面口径</dd></div><div><dt>租赁备案</dt><dd>公告与尽调记录不一致</dd></div><div><dt>二押余额</dt><dd>缺少最新证明</dd></div><div><dt>地址表述</dt><dd>3 处文书表述差异</dd></div></dl></article>
        </div>
      </section>
    </div>
    <section class="panel section-space reveal-item"><div class="panel-header"><div><h2>尽调完整度</h2><p>38 项标准尽调事项，其中 7 项待复核</p></div><span class="status-pill warn">82% 完成</span></div><div class="chart"><div class="chart-bar-wrap"><b>100%</b><div class="chart-bar" style="height:100%"></div><span>主体资格</span></div><div class="chart-bar-wrap"><b>94%</b><div class="chart-bar" style="height:94%"></div><span>债权有效性</span></div><div class="chart-bar-wrap"><b>86%</b><div class="chart-bar" style="height:86%"></div><span>抵押信息</span></div><div class="chart-bar-wrap"><b>72%</b><div class="chart-bar" style="height:72%"></div><span>司法程序</span></div><div class="chart-bar-wrap"><b>58%</b><div class="chart-bar" style="height:58%"></div><span>税务事项</span></div><div class="chart-bar-wrap"><b>67%</b><div class="chart-bar" style="height:67%"></div><span>占有腾退</span></div></div></section>
  </section>`;

const valuationView = () => `
  <section class="view" data-view="valuation">
    ${pageHeading("Transparent Valuation", "投资测算", "通过市场法、收益法与风险折价反推最高可承受买价，每一个参数都可回溯到来源与复核人。", `<button class="button" id="save-scenario">保存为方案 B</button><button class="button primary" id="calc-button">${icon("valuation")} 重新测算</button>`)}
    <div class="two-column">
      <section class="panel reveal-item">
        <div class="panel-header"><div><h2>测算参数</h2><p>调整参数即时查看结果变化</p></div><span class="data-badge">SCENARIO A</span></div>
        <div class="form-section"><div class="form-grid">
          <div class="field"><label>市场可比估值 <output id="market-output">5,200 万</output></label><input id="market-value" type="range" min="3500" max="7000" step="50" value="5200" /></div>
          <div class="field"><label>年度有效租金 <output id="rent-output">420 万</output></label><input id="annual-rent" type="range" min="200" max="650" step="10" value="420" /></div>
          <div class="field"><label>出租率 <output id="occupancy-output">82%</output></label><input id="occupancy" type="range" min="50" max="100" step="1" value="82" /></div>
          <div class="field"><label>资本化率 <output id="cap-output">8.5%</output></label><input id="cap-rate" type="range" min="5" max="14" step="0.1" value="8.5" /></div>
          <div class="field"><label>司法风险折价 <output id="risk-output">12%</output></label><input id="risk-discount" type="range" min="0" max="30" step="1" value="12" /></div>
          <div class="field"><label>目标利润率 <output id="profit-output">20%</output></label><input id="target-profit" type="range" min="8" max="35" step="1" value="20" /></div>
          <div class="field"><label>税费及腾退成本</label><input id="extra-cost" type="number" value="380" /></div>
          <div class="field"><label>融资成本</label><input id="fund-cost" type="number" value="260" /></div>
        </div></div>
        <div class="panel-header"><div><h2>参数证据</h2><p>来源、采集时间与复核状态</p></div></div>
        <div class="panel-pad evidence-list">
          <div class="evidence-item"><div><strong>市场可比估值</strong><p>4 个周边成交案例 · 2026-08-06 采集</p></div><span class="status-pill good">已复核</span></div>
          <div class="evidence-item"><div><strong>有效租金</strong><p>2 份真实租约 + 3 份招商台账</p></div><span class="status-pill warn">待补 1 项</span></div>
          <div class="evidence-item"><div><strong>司法风险折价</strong><p>租赁、欠税与腾退三类风险加权</p></div><span class="status-pill info">模型计算</span></div>
        </div>
      </section>
      <div>
        <section class="calc-result reveal-item"><div class="result-label">RECOMMENDED MAXIMUM BID · 建议最高竞买价</div><div class="result-main" id="max-bid">¥ 3,135 万</div><div class="result-sub">较综合谨慎估值折价 <span id="margin-result">30.0%</span> · 不含不可预见税费</div><div class="result-grid"><div class="result-cell"><span>收益法估值</span><strong id="income-result">¥ 4,052 万</strong></div><div class="result-cell"><span>综合谨慎估值</span><strong id="blended-result">¥ 4,481 万</strong></div><div class="result-cell"><span>预计总投入</span><strong id="total-result">¥ 3,775 万</strong></div><div class="result-cell"><span>预计项目 IRR</span><strong>21.4%</strong></div></div></section>
        <section class="panel section-space reveal-item"><div class="panel-header"><div><h2>敏感性分析</h2><p>资本化率变化对最高竞买价的影响</p></div><span class="status-pill info">动态</span></div><div class="chart" id="sensitivity-chart"><div class="chart-bar-wrap"><b>2,742</b><div class="chart-bar" style="height:48%"></div><span>10.5%</span></div><div class="chart-bar-wrap"><b>2,918</b><div class="chart-bar" style="height:58%"></div><span>9.5%</span></div><div class="chart-bar-wrap"><b>3,135</b><div class="chart-bar" style="height:72%"></div><span>8.5%</span></div><div class="chart-bar-wrap"><b>3,410</b><div class="chart-bar" style="height:86%"></div><span>7.5%</span></div><div class="chart-bar-wrap"><b>3,768</b><div class="chart-bar" style="height:100%"></div><span>6.5%</span></div></div></section>
        <section class="panel panel-pad section-space reveal-item"><div class="risk-list"><div class="risk-item"><span class="risk-level high"></span><div><strong>竞买纪律提示</strong><p>超过 3,135 万时，安全边际将低于 30%，需要投委会二次确认。</p></div><span class="tag danger">强约束</span></div></div></section>
      </div>
    </div>
  </section>`;

const judicialView = () => `
  <section class="view" data-view="judicial">
    ${pageHeading("Judicial Risk Graph", "司法风控", "将案件、抵押、查封、租赁与优先受偿事项组织成时间轴和权利顺位，系统识别与律师意见分层展示。", `<button class="button" id="evidence-list-button">取证清单 · 7</button><button class="button primary" id="lawyer-review">提交律师复核</button>`)}
    <div class="kpi-grid">
      <article class="kpi-card reveal-item"><div class="kpi-label"><span>综合司法风险</span></div><div class="kpi-value">68 / 100</div><div class="kpi-change neutral">中高风险</div></article>
      <article class="kpi-card reveal-item"><div class="kpi-label"><span>关联案件</span></div><div class="kpi-value">3 件</div><div class="kpi-change">首封法院已确认</div></article>
      <article class="kpi-card reveal-item"><div class="kpi-label"><span>权利与负担</span></div><div class="kpi-value">6 项</div><div class="kpi-change neutral">2 项仍待核实</div></article>
      <article class="kpi-card reveal-item"><div class="kpi-label"><span>律师复核</span></div><div class="kpi-value">76%</div><div class="kpi-change">13 项结论已确认</div></article>
    </div>
    <div class="two-column">
      <section class="panel reveal-item"><div class="panel-header"><div><h2>权利受偿顺位</h2><p>基于当前已复核材料的初步排序</p></div><span class="status-pill warn">待律师确认</span></div><div class="rights-stack"><div class="right-layer"><span class="right-order">01</span><div><strong>建设工程价款优先权</strong><small>尚未发现申报，建议取证排除</small></div><span class="tag warn">待排除</span></div><div class="right-layer"><span class="right-order">02</span><div><strong>第一顺位抵押权</strong><small>某资产管理公司 · 登记金额 3,200 万</small></div><span class="tag good">已确认</span></div><div class="right-layer"><span class="right-order">03</span><div><strong>税款优先事项</strong><small>历史欠税数额未取得税务机关书面口径</small></div><span class="tag danger">高风险</span></div><div class="right-layer"><span class="right-order">04</span><div><strong>第二顺位抵押权</strong><small>自然人债权人 · 实际余额待核</small></div><span class="tag warn">待核实</span></div><div class="right-layer"><span class="right-order">05</span><div><strong>普通债权与轮候查封</strong><small>2 家债权人 · 轮候顺序已确认</small></div><span class="tag info">已归集</span></div></div></section>
      <section class="panel reveal-item"><div class="panel-header"><div><h2>案件时间轴</h2><p>来源：执行裁定、拍卖公告与当事人材料</p></div><span class="data-badge">3 CASES</span></div><div class="timeline"><div class="timeline-item"><span class="timeline-dot"></span><time>2023-08-14</time><div><strong>抵押权设立</strong><p>第一顺位抵押登记，债权数额 3,200 万元。</p></div></div><div class="timeline-item"><span class="timeline-dot"></span><time>2024-03-18</time><div><strong>执行立案</strong><p>成都市中级人民法院立案执行。</p></div></div><div class="timeline-item"><span class="timeline-dot"></span><time>2024-04-02</time><div><strong>首轮查封</strong><p>标的物完成首封，期限至 2027 年 4 月。</p></div></div><div class="timeline-item"><span class="timeline-dot"></span><time>2025-11-06</time><div><strong>轮候查封登记</strong><p>新增 2 名轮候债权人，不影响首封处置。</p></div></div><div class="timeline-item"><span class="timeline-dot"></span><time>2026-07-28</time><div><strong>发布一拍公告</strong><p>起拍价 2,980 万元，保证金 300 万元。</p></div></div><div class="timeline-item"><span class="timeline-dot"></span><time>2026-08-16</time><div><strong>拍卖日</strong><p>建议最高竞买价 3,135 万元。</p></div></div></div></section>
    </div>
    <section class="panel panel-pad section-space reveal-item"><div class="risk-list"><div class="risk-item"><span class="risk-level high"></span><div><strong>历史欠税可能影响过户</strong><p>拍卖公告仅写明税费各自承担，需向主管税务机关获得书面口径。</p></div><span class="tag danger">高风险</span></div><div class="risk-item"><span class="risk-level"></span><div><strong>存在拍卖前租赁关系</strong><p>需核实合同真实性、租金支付及实际占有，评估“买卖不破租赁”风险。</p></div><span class="tag warn">中风险</span></div><div class="risk-item"><span class="risk-level low"></span><div><strong>首封法院与抵押顺位清晰</strong><p>已核验首封裁定与不动产登记信息，暂未发现程序冲突。</p></div><span class="tag good">低风险</span></div></div></section>
  </section>`;

const taxView = () => `
  <section class="view" data-view="tax">
    ${pageHeading("Tax Rule Engine", "税务规则", "税费结果不是写死的税率，而是由地区、主体、资产、取得方式与生效日期共同匹配的规则包。", `<button class="button" id="new-rule">${icon("plus")} 新建规则</button><button class="button primary" id="tax-calc-button">执行测算</button>`)}
    <div class="two-column">
      <section class="panel reveal-item"><div class="panel-header"><div><h2>交易场景</h2><p>当前匹配地区：四川省 · 成都市 · 锦江区</p></div><span class="status-pill good">4 条规则已匹配</span></div><div class="form-section"><div class="form-grid"><div class="field"><label>资产类型</label><select id="tax-asset"><option>商业不动产</option><option>住宅</option><option>土地使用权</option><option>债权转让</option></select></div><div class="field"><label>交易方式</label><select><option>司法拍卖</option><option>协议转让</option><option>抵债过户</option></select></div><div class="field"><label>出让主体</label><select><option>企业</option><option>自然人</option></select></div><div class="field"><label>受让主体</label><select><option>企业</option><option>自然人</option></select></div><div class="field"><label>成交价（万元）</label><input id="tax-price" type="number" value="3135" /></div><div class="field"><label>评估价（万元）</label><input id="tax-appraisal" type="number" value="5680" /></div><div class="field"><label>原始取得成本（万元）</label><input id="tax-cost" type="number" value="1900" /></div><div class="field"><label>取得时间</label><input type="text" value="2018-04-16" /></div></div></div></section>
      <section class="calc-result reveal-item"><div class="result-label">ESTIMATED TAX & FEES · 预计交易税费</div><div class="result-main" id="tax-total">¥ 347.97 万</div><div class="result-sub">结果含确定值、区间值与待核实值，不构成税务意见。</div><div class="result-grid"><div class="result-cell"><span>增值税及附加</span><strong id="tax-vat">¥ 175.56 万</strong></div><div class="result-cell"><span>买方契税</span><strong id="tax-deed">¥ 94.05 万</strong></div><div class="result-cell"><span>印花税</span><strong id="tax-stamp">¥ 3.14 万</strong></div><div class="result-cell"><span>土地增值税预征</span><strong id="tax-land">¥ 75.24 万</strong></div></div></section>
    </div>
    <div class="two-column section-space">
      <section class="panel reveal-item"><div class="panel-header"><div><h2>已匹配规则</h2><p>匹配逻辑、生效期间与发布状态</p></div><span class="data-badge">RULE SET CN-SC-CD-04</span></div><div class="panel-pad rule-list"><div class="rule-item"><div><strong>司法拍卖不动产增值税</strong><p>一般计税 / 简易计税方法需按取得时间匹配</p><div class="rule-meta"><span>VAT-SC-2026-04</span><span>2026-01-01 生效</span><span>四川省</span></div></div><span class="status-pill good">已发布</span></div><div class="rule-item"><div><strong>买方契税计税依据</strong><p>按成交价与核定价孰高作为计税依据</p><div class="rule-meta"><span>DEED-CD-2025-02</span><span>3%</span><span>成都市</span></div></div><span class="status-pill good">已发布</span></div><div class="rule-item"><div><strong>历史欠税与过户前置</strong><p>缺少锦江区主管税务机关对本项目的书面口径</p><div class="rule-meta"><span>EXCEPTION-019</span><span>项目级</span><span>证据待补</span></div></div><span class="status-pill danger">待核实</span></div></div></section>
      <section class="panel reveal-item"><div class="panel-header"><div><h2>规则证据链</h2><p>政策来源、口径文件与审核记录</p></div><span class="status-pill warn">3 / 4 已确认</span></div><div class="panel-pad evidence-list"><div class="evidence-item"><div><strong>不动产交易税收政策汇编</strong><p>国家税务总局四川省税务局 · 2026-01-18 采集</p></div><span class="status-pill good">顾问已复核</span></div><div class="evidence-item"><div><strong>司法拍卖税费承担条款</strong><p>拍卖公告第 8 页第四条 · 原文可定位</p></div><span class="status-pill good">法务已复核</span></div><div class="evidence-item"><div><strong>锦江区历史欠税处理口径</strong><p>已提交书面咨询，预计 3 个工作日内回复</p></div><span class="status-pill warn">待取证</span></div></div></section>
    </div>
  </section>`;

const rentView = () => `
  <section class="view" data-view="rent">
    ${pageHeading("Comparable Rent Intelligence", "租金案例", "将真实租约、招商台账、经纪平台与公开挂牌分级管理，为收益法估值提供有来源、有日期的可比证据。", `<button class="button" id="rent-filter">筛选可比案例</button><button class="button primary" id="new-rent-case">${icon("plus")} 新增案例</button>`)}
    <div class="dashboard-grid">
      <section class="panel map-board reveal-item" aria-label="项目周边租金可比案例地图">
        <button class="map-marker target" style="left:48%;top:43%" data-map="项目标的 · 锦江区商业 9,012㎡"><span>项</span></button>
        <button class="map-marker" style="left:28%;top:24%" data-map="春熙路临街商业 · 138元/㎡/月"><span>A</span></button>
        <button class="map-marker" style="left:67%;top:29%" data-map="总府路裙楼商业 · 72元/㎡/月"><span>B</span></button>
        <button class="map-marker" style="left:72%;top:68%" data-map="红星路综合体 · 58元/㎡/月"><span>C</span></button>
        <button class="map-marker" style="left:20%;top:66%" data-map="盐市口存量商业 · 102元/㎡/月"><span>D</span></button>
        <div class="map-legend">● 项目标的 · ● A/B/C/D 级案例</div>
      </section>
      <div>
        <div class="kpi-grid" style="grid-template-columns:repeat(2,minmax(0,1fr));margin-top:0"><article class="kpi-card reveal-item"><div class="kpi-label"><span>调整后中位租金</span></div><div class="kpi-value">¥ 86</div><div class="kpi-change neutral">元/㎡/月</div></article><article class="kpi-card reveal-item"><div class="kpi-label"><span>平均出租率</span></div><div class="kpi-value">82%</div><div class="kpi-change">4 个核心案例</div></article><article class="kpi-card reveal-item"><div class="kpi-label"><span>核心可比案例</span></div><div class="kpi-value">4</div><div class="kpi-change">A 级 1 个 · B 级 1 个</div></article><article class="kpi-card reveal-item"><div class="kpi-label"><span>数据最新日期</span></div><div class="kpi-value">08/06</div><div class="kpi-change neutral">2 条待人工回访</div></article></div>
        <section class="panel reveal-item"><div class="panel-header"><div><h2>估值采用建议</h2><p>根据可信度与可比性调整</p></div></div><div class="panel-pad"><div class="calc-result"><div class="result-label">RECOMMENDED EFFECTIVE RENT</div><div class="result-main">¥ 78.6</div><div class="result-sub">元/㎡/月 · 已扣除免租期、空置与楼层差异</div></div></div></section>
      </div>
    </div>
    <section class="panel section-space reveal-item"><div class="panel-header"><div><h2>可比案例清单</h2><p>A 级：真实租约与流水 · B 级：招商台账 · C/D 级：挂牌与公开数据</p></div><span class="data-badge">12 CASES</span></div><div class="table-wrap"><table class="data-table"><thead><tr><th>案例</th><th>距离</th><th>面积 / 楼层</th><th>挂牌租金</th><th>有效租金</th><th>数据等级</th><th>可比性调整</th><th>采集日期</th></tr></thead><tbody><tr><td><span class="cell-main">春熙路临街商业</span><span class="cell-sub">真实租赁合同 + 收款流水</span></td><td>0.4km</td><td>780㎡ · 1F</td><td class="mono">¥ 138</td><td class="mono">¥ 126</td><td><span class="tag good">A 级</span></td><td>-18%</td><td>2026-07-12</td></tr><tr><td><span class="cell-main">总府路裙楼商业</span><span class="cell-sub">业主招商台账</span></td><td>0.8km</td><td>2,100㎡ · 2F</td><td class="mono">¥ 72</td><td class="mono">¥ 68</td><td><span class="tag info">B 级</span></td><td>+12%</td><td>2026-08-03</td></tr><tr><td><span class="cell-main">红星路综合体</span><span class="cell-sub">经纪平台 + 电话核验</span></td><td>1.2km</td><td>1,650㎡ · 3F</td><td class="mono">¥ 58</td><td class="mono">¥ 51</td><td><span class="tag warn">C 级</span></td><td>+25%</td><td>2026-08-06</td></tr><tr><td><span class="cell-main">盐市口存量商业</span><span class="cell-sub">公开网络挂牌</span></td><td>1.4km</td><td>980㎡ · 1F</td><td class="mono">¥ 102</td><td class="mono">¥ 88</td><td><span class="tag danger">D 级</span></td><td>-12%</td><td>2026-08-01</td></tr></tbody></table></div></section>
  </section>`;

const workflowView = () => `
  <section class="view" data-view="workflow">
    ${pageHeading("Lifecycle Operations", "投 · 融 · 管 · 退", "四条主线把投资决策、融资结构、交割运营与退出回款放在同一个项目语境中，沉淀真实处置周期与回收率。", `<button class="button" id="workflow-view">切换时间轴</button><button class="button primary" id="new-task">${icon("plus")} 新建任务</button>`)}
    <div class="process-track reveal-item">
      <section class="process-lane"><header><span class="lane-char">投</span><span class="lane-status">6 / 8 DONE</span></header><article class="task-card done"><strong>项目入库</strong><p>债权、抵押物与交易基本信息</p><footer><span>况先生</span><span>✓ 已完成</span></footer></article><article class="task-card done"><strong>法律尽调</strong><p>31/38 项已复核，司法时间轴已形成</p><footer><span>李律师</span><span>82%</span></footer></article><article class="task-card active"><strong>投资测算</strong><p>补充租金案例与税务书面口径</p><footer><span>况先生</span><span>08/12</span></footer></article><article class="task-card"><strong>投委会审批</strong><p>生成上会材料、底价与竞买纪律</p><footer><span>投委会</span><span>08/14</span></footer></article></section>
      <section class="process-lane"><header><span class="lane-char">融</span><span class="lane-status">DESIGNING</span></header><article class="task-card active"><strong>资金结构</strong><p>自有资金 40% · 优先资金 60%</p><footer><span>财务顾问</span><span>测算中</span></footer></article><article class="task-card"><strong>优先资金方</strong><p>期限 18 个月，目标成本 8.0% / 年</p><footer><span>待定</span><span>2 家洽谈</span></footer></article><article class="task-card"><strong>SPV 架构</strong><p>项目公司与有限合伙方案对比</p><footer><span>外部顾问</span><span>待启动</span></footer></article></section>
      <section class="process-lane"><header><span class="lane-char">管</span><span class="lane-status">PLAYBOOK</span></header><article class="task-card"><strong>交割与过户</strong><p>付款节点、文件交接与产权过户</p><footer><span>项目经理</span><span>预案</span></footer></article><article class="task-card"><strong>腾退与清场</strong><p>建议预留 3 个月周期与 180 万成本</p><footer><span>处置团队</span><span>中风险</span></footer></article><article class="task-card"><strong>招商与改造</strong><p>首层业态调整，目标出租率 90%</p><footer><span>运营团队</span><span>预案</span></footer></article><article class="task-card"><strong>现金流管理</strong><p>租金、改造、融资与处置收支</p><footer><span>财务</span><span>模板</span></footer></article></section>
      <section class="process-lane"><header><span class="lane-char">退</span><span class="lane-status">2 OPTIONS</span></header><article class="task-card active"><strong>方案 A · 整体转让</strong><p>运营 12 个月后整体转让，预估 IRR 21.4%</p><footer><span>推荐</span><span>18 个月</span></footer></article><article class="task-card"><strong>方案 B · 分拆销售</strong><p>分业态拆分销售，收益更高但周期更长</p><footer><span>备选</span><span>30 个月</span></footer></article><article class="task-card"><strong>退出条件监测</strong><p>出租率 ≥ 88% · NOI ≥ 410 万 / 年</p><footer><span>系统监控</span><span>2 个指标</span></footer></article></section>
    </div>
    <div class="kpi-grid section-space"><article class="kpi-card reveal-item"><div class="kpi-label"><span>计划总周期</span></div><div class="kpi-value">18 个月</div><div class="kpi-change neutral">交割 2 月 · 运营 12 月 · 退出 4 月</div></article><article class="kpi-card reveal-item"><div class="kpi-label"><span>资金峰值</span></div><div class="kpi-value">¥ 3,920 万</div><div class="kpi-change neutral">预计发生于第 4 个月</div></article><article class="kpi-card reveal-item"><div class="kpi-label"><span>预计回收率</span></div><div class="kpi-value">136%</div><div class="kpi-change">含运营期现金流</div></article><article class="kpi-card reveal-item"><div class="kpi-label"><span>当前待办</span></div><div class="kpi-value">11</div><div class="kpi-change neutral">3 项高优先级</div></article></div>
  </section>`;

const reportsView = () => `
  <section class="view" data-view="reports">
    ${pageHeading("Decision Documents", "报告中心", "从项目最新的已复核数据生成尽调、投委、司法与物权报告，每份报告固定数据快照、规则版本与生成记录。", `<button class="button" id="template-manage">模板管理</button><button class="button primary" id="generate-report">${icon("spark")} 生成新报告</button>`)}
    <div class="report-grid">
      ${[
        ["投委会上会材料", "项目概况、尽调发现、测算结论、风险与竞买纪律。", "08/09 · v0.8", "待更新"],
        ["债权尽调报告", "债权有效性、担保措施、诉讼时效与执行现状。", "08/08 · v1.2", "已复核"],
        ["司法风险分析", "案件时间轴、权利顺位、风险标签与建议取证清单。", "08/08 · v1.0", "律师复核"],
        ["投资测算报告", "市场法、收益法、谨慎估值、最高竞买价与敏感性。", "08/09 · v0.6", "最新"],
        ["物权推介报告", "抵押物现状、区位市场、租金案例与运营提升空间。", "08/06 · v0.4", "草稿"],
        ["项目管理月报", "投融管退进展、待办、现金流、风险变化与下月计划。", "07/31 · v1.0", "已归档"],
      ].map(([title, desc, version, status], index) => `<article class="report-card reveal-item"><div class="report-preview"><div class="paper-mark">特资智投系统 · 星河数科 · ${String(index + 1).padStart(2, "0")}</div><div class="paper-title">${title}</div><div class="paper-lines"><i></i><i style="width:82%"></i><i style="width:56%"></i></div></div><h3>${title}</h3><p>${desc}</p><div class="report-footer"><span class="status-pill ${status === "最新" || status === "已复核" ? "good" : status === "待更新" ? "warn" : "info"}">${status}</span><button class="button small report-preview-button" data-report="${title}">${version} · 预览</button></div></article>`).join("")}
    </div>
    <section class="panel section-space reveal-item"><div class="panel-header"><div><h2>生成记录</h2><p>报告、数据快照、模型与规则版本的完整映射</p></div><span class="data-badge">AUDIT TRAIL</span></div><div class="table-wrap"><table class="data-table"><thead><tr><th>生成时间</th><th>报告名称</th><th>生成人</th><th>数据快照</th><th>模型 / 规则版本</th><th>输出格式</th></tr></thead><tbody><tr><td>2026-08-09 15:20</td><td>投资测算报告</td><td>况先生</td><td class="mono">SNAP-0819-04</td><td class="mono">VAL v0.6 / TAX v04</td><td><span class="tag good">Word</span> <span class="tag">PDF</span></td></tr><tr><td>2026-08-08 18:42</td><td>司法风险分析</td><td>李律师</td><td class="mono">SNAP-0818-11</td><td class="mono">LEGAL v0.8</td><td><span class="tag">PDF</span></td></tr><tr><td>2026-08-08 11:05</td><td>债权尽调报告</td><td>AI 生成 / 李律师复核</td><td class="mono">SNAP-0818-03</td><td class="mono">DOC v1.2</td><td><span class="tag good">Word</span></td></tr></tbody></table></div></section>
  </section>`;

const views: Record<ViewId, () => string> = {
  overview: overviewView,
  projects: projectsView,
  documents: documentsView,
  valuation: valuationView,
  judicial: judicialView,
  tax: taxView,
  rent: rentView,
  workflow: workflowView,
  reports: reportsView,
};

function formatNumber(value: number, decimals = 0) {
  return value.toLocaleString("zh-CN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function renderView(view: ViewId) {
  viewContext?.revert();
  viewRoot.innerHTML = views[view]();
  window.scrollTo({ top: 0, behavior: reducedMotion ? "auto" : "smooth" });

  document.querySelectorAll<HTMLElement>("[data-nav]").forEach((element) => {
    element.classList.toggle("active", element.dataset.nav === view);
  });
  sidebar.classList.remove("open");

  viewContext = gsap.context(() => {
    if (!reducedMotion) {
      gsap.fromTo(
        ".reveal-item",
        { autoAlpha: 0, y: 18 },
        { autoAlpha: 1, y: 0, duration: 0.62, ease: "power3.out", stagger: 0.045, clearProps: "transform,opacity,visibility" },
      );
      if (viewRoot.querySelector(".chart-bar")) {
        gsap.fromTo(
          ".chart-bar",
          { scaleY: 0, transformOrigin: "bottom" },
          { scaleY: 1, duration: 0.85, ease: "power3.out", stagger: 0.06 },
        );
      }
      if (viewRoot.querySelector(".right-layer, .task-card")) {
        gsap.fromTo(
          ".right-layer, .task-card",
          { x: -12, autoAlpha: 0 },
          { x: 0, autoAlpha: 1, duration: 0.48, ease: "power2.out", stagger: 0.04 },
        );
      }
    }

    document.querySelectorAll<HTMLElement>(".counter").forEach((counter) => {
      const target = Number(counter.dataset.value ?? 0);
      const decimals = Number(counter.dataset.decimals ?? 0);
      if (reducedMotion) {
        counter.textContent = formatNumber(target, decimals);
        return;
      }
      const state = { value: 0 };
      gsap.to(state, {
        value: target,
        duration: 1.3,
        ease: "power2.out",
        onUpdate: () => {
          counter.textContent = formatNumber(state.value, decimals);
        },
      });
    });

    if (view === "overview" && !reducedMotion) {
      gsap.to(".ring-one", { rotation: 360, duration: 34, repeat: -1, ease: "none" });
      gsap.to(".ring-two", { rotation: -360, duration: 24, repeat: -1, ease: "none" });
      const projectDot = document.querySelector<HTMLElement>(".project-dot");
      if (projectDot) gsap.to(projectDot, { scale: 1.35, duration: 1.2, repeat: -1, yoyo: true, ease: "sine.inOut" });
    }
  }, viewRoot);

  bindViewInteractions(view);
}

function bindViewInteractions(view: ViewId) {
  document.querySelectorAll<HTMLButtonElement>(".filter-chip").forEach((chip) => {
    chip.addEventListener("click", () => {
      document.querySelectorAll(".filter-chip").forEach((item) => item.classList.remove("active"));
      chip.classList.add("active");
      showToast(`已切换到“${chip.textContent?.trim()}”项目视图`);
    });
  });

  if (view === "projects") bindProjectInteractions();
  if (view === "documents") bindDocumentInteractions();
  if (view === "valuation") bindValuationInteractions();
  if (view === "judicial") bindJudicialInteractions();
  if (view === "tax") bindTaxInteractions();
  if (view === "rent") bindRentInteractions();
  if (view === "workflow") bindWorkflowInteractions();
  if (view === "reports") bindReportInteractions();
}

function bindProjectInteractions() {
  document.querySelector("#new-project")?.addEventListener("click", () => {
    openModal(`
      <div class="modal-header"><h2>新建特殊资产项目</h2><button class="icon-button modal-close">${icon("close")}</button></div>
      <div class="modal-body"><div class="form-grid"><div class="field"><label>项目名称</label><input placeholder="请输入项目名称" /></div><div class="field"><label>交易类型</label><select><option>债权收购</option><option>司法拍卖</option><option>破产重整</option><option>抵债资产</option></select></div><div class="field"><label>所在地区</label><input placeholder="省 / 市 / 区" /></div><div class="field"><label>项目负责人</label><select><option>况先生</option><option>王经理</option><option>周经理</option></select></div></div><div class="drop-zone section-space">${icon("upload")}<div><strong>可直接上传拍卖公告或项目资料</strong><span>AI 将生成项目档案初稿</span></div></div></div>
      <div class="modal-actions"><button class="button modal-close">取消</button><button class="button primary modal-confirm">创建项目</button></div>`);
  });
  document.querySelector("#import-project")?.addEventListener("click", () => showToast("已打开 Excel 导入向导（演示）"));
}

function bindDocumentInteractions() {
  const dropZone = document.querySelector<HTMLElement>("#drop-zone");
  const analyzeButton = document.querySelector<HTMLButtonElement>("#analyze-files");
  dropZone?.addEventListener("click", () => showToast("已模拟上传《税务局书面回复.pdf》"));
  dropZone?.addEventListener("keydown", (event) => {
    if (event.key === "Enter" || event.key === " ") dropZone.click();
  });
  analyzeButton?.addEventListener("click", () => {
    analyzeButton.disabled = true;
    analyzeButton.textContent = "AI 正在归集证据…";
    gsap.to("#extraction-results .extraction-card", { autoAlpha: 0.35, duration: reducedMotion ? 0 : 0.25 });
    window.setTimeout(() => {
      analyzeButton.disabled = false;
      analyzeButton.innerHTML = `${icon("spark")} 重新分析`;
      gsap.to("#extraction-results .extraction-card", { autoAlpha: 1, duration: reducedMotion ? 0 : 0.45, stagger: 0.08 });
      showToast("分析完成：新增 1 条税务证据，2 项冲突待复核");
    }, 1100);
  });
  document.querySelector("#evidence-export")?.addEventListener("click", () => showToast("尽调证据清单已生成（演示）"));
}

function bindValuationInteractions() {
  const ids = ["market-value", "annual-rent", "occupancy", "cap-rate", "risk-discount", "target-profit", "extra-cost", "fund-cost"];
  ids.forEach((id) => document.querySelector(`#${id}`)?.addEventListener("input", updateValuation));
  document.querySelector("#calc-button")?.addEventListener("click", () => {
    updateValuation();
    showToast("测算已更新，结果已绑定当前参数版本");
  });
  document.querySelector("#save-scenario")?.addEventListener("click", () => showToast("已保存为“方案 B · 谨慎情景”"));
}

function inputNumber(id: string) {
  return Number(document.querySelector<HTMLInputElement>(`#${id}`)?.value ?? 0);
}

function setText(id: string, text: string) {
  const element = document.querySelector<HTMLElement>(`#${id}`);
  if (element) element.textContent = text;
}

function updateValuation() {
  const market = inputNumber("market-value");
  const rent = inputNumber("annual-rent");
  const occupancy = inputNumber("occupancy") / 100;
  const cap = inputNumber("cap-rate") / 100;
  const risk = inputNumber("risk-discount") / 100;
  const profit = inputNumber("target-profit") / 100;
  const extra = inputNumber("extra-cost");
  const fund = inputNumber("fund-cost");
  const income = cap ? (rent * occupancy) / cap : 0;
  const blended = (market * 0.55 + income * 0.45) * (1 - risk);
  const maxBid = Math.max(blended / (1 + profit) - extra - fund, 0);
  const margin = blended ? (1 - maxBid / blended) * 100 : 0;

  setText("market-output", `${formatNumber(market)} 万`);
  setText("rent-output", `${formatNumber(rent)} 万`);
  setText("occupancy-output", `${formatNumber(occupancy * 100)}%`);
  setText("cap-output", `${formatNumber(cap * 100, 1)}%`);
  setText("risk-output", `${formatNumber(risk * 100)}%`);
  setText("profit-output", `${formatNumber(profit * 100)}%`);
  setText("max-bid", `¥ ${formatNumber(maxBid)} 万`);
  setText("margin-result", `${formatNumber(margin, 1)}%`);
  setText("income-result", `¥ ${formatNumber(income)} 万`);
  setText("blended-result", `¥ ${formatNumber(blended)} 万`);
  setText("total-result", `¥ ${formatNumber(maxBid + extra + fund)} 万`);
  if (!reducedMotion) gsap.fromTo("#max-bid", { scale: 0.97, color: "#edf1e9" }, { scale: 1, color: "#d7b979", duration: 0.35, ease: "power2.out" });
}

function bindJudicialInteractions() {
  document.querySelector("#lawyer-review")?.addEventListener("click", () => showToast("已提交给项目律师，预计完成时间：08/11 18:00"));
  document.querySelector("#evidence-list-button")?.addEventListener("click", () => openEvidenceModal());
}

function openEvidenceModal() {
  openModal(`<div class="modal-header"><h2>司法尽调取证清单</h2><button class="icon-button modal-close">${icon("close")}</button></div><div class="modal-body"><div class="evidence-list"><div class="evidence-item"><div><strong>01 · 主管税务机关书面回复</strong><p>确认历史欠税、过户前置及负担主体</p></div><span class="tag danger">高优先级</span></div><div class="evidence-item"><div><strong>02 · 工程价款结清证明</strong><p>排除建设工程价款优先权</p></div><span class="tag danger">高优先级</span></div><div class="evidence-item"><div><strong>03 · 租赁合同原件与流水</strong><p>核验合同真实性、签订时间与实际履行</p></div><span class="tag warn">本周</span></div><div class="evidence-item"><div><strong>04 · 第二顺位抵押债权余额函</strong><p>核实实际本息与优先受偿范围</p></div><span class="tag warn">本周</span></div></div></div><div class="modal-actions"><button class="button modal-close">关闭</button><button class="button primary modal-confirm">导出清单</button></div>`);
}

function bindTaxInteractions() {
  document.querySelector("#tax-calc-button")?.addEventListener("click", updateTax);
  ["tax-price", "tax-cost", "tax-appraisal"].forEach((id) => document.querySelector(`#${id}`)?.addEventListener("input", updateTax));
  document.querySelector("#new-rule")?.addEventListener("click", () => showToast("规则编辑器已打开（演示）"));
}

function updateTax() {
  const price = inputNumber("tax-price");
  const appraisal = inputNumber("tax-appraisal");
  const basis = Math.max(price, appraisal * 0.55);
  const vat = price * 0.05 * 1.12;
  const deed = basis * 0.03;
  const stamp = price * 0.001;
  const land = price * 0.024;
  const total = vat + deed + stamp + land;
  setText("tax-total", `¥ ${formatNumber(total, 2)} 万`);
  setText("tax-vat", `¥ ${formatNumber(vat, 2)} 万`);
  setText("tax-deed", `¥ ${formatNumber(deed, 2)} 万`);
  setText("tax-stamp", `¥ ${formatNumber(stamp, 2)} 万`);
  setText("tax-land", `¥ ${formatNumber(land, 2)} 万`);
  showToast("已按地区、主体与交易方式重新匹配 4 条规则");
}

function bindRentInteractions() {
  document.querySelectorAll<HTMLElement>(".map-marker").forEach((marker) => {
    marker.addEventListener("click", () => {
      showToast(marker.dataset.map ?? "已选中租金案例");
      if (!reducedMotion) gsap.fromTo(marker, { scale: 0.75 }, { scale: 1, duration: 0.45, ease: "back.out(2)" });
    });
  });
  document.querySelector("#new-rent-case")?.addEventListener("click", () => showToast("已打开租金案例录入表（演示）"));
  document.querySelector("#rent-filter")?.addEventListener("click", () => showToast("已应用：1.5km 内 · 商业 · 近 12 个月"));
}

function bindWorkflowInteractions() {
  document.querySelector("#new-task")?.addEventListener("click", () => showToast("新建任务已添加到“投”阶段（演示）"));
  document.querySelector("#workflow-view")?.addEventListener("click", () => showToast("时间轴视图将展示完整项目节点（演示）"));
}

function bindReportInteractions() {
  document.querySelectorAll<HTMLButtonElement>(".report-preview-button").forEach((button) => {
    button.addEventListener("click", () => openReportModal(button.dataset.report ?? "项目报告"));
  });
  document.querySelector("#generate-report")?.addEventListener("click", () => showToast("已完成数据预检，可生成投委会上会材料"));
  document.querySelector("#template-manage")?.addEventListener("click", () => showToast("已加载 6 个机构报告模板"));
}

function openReportModal(title: string) {
  openModal(`<div class="modal-header"><h2>${title}</h2><button class="icon-button modal-close">${icon("close")}</button></div><div class="modal-body"><div class="report-preview" style="height:320px;padding:36px"><div class="paper-mark">特资智投系统 · 星河数科 · 机密</div><div class="paper-title" style="font-size:28px;width:85%;margin-top:36px">${title}</div><div class="paper-lines" style="margin-top:28px;gap:10px"><i></i><i style="width:88%"></i><i style="width:73%"></i><i style="width:92%"></i><i style="width:60%"></i></div></div><p style="color:var(--muted);font-size:10px;line-height:1.7">本预览展示未来的报告生成能力。正式报告将固定当前数据快照、参数、规则和模型版本。</p></div><div class="modal-actions"><button class="button modal-close">关闭</button><button class="button">导出 Word</button><button class="button primary modal-confirm">导出 PDF</button></div>`);
}

function openModal(content: string) {
  modal.innerHTML = content;
  modalBackdrop.style.display = "grid";
  modalBackdrop.setAttribute("aria-hidden", "false");
  if (!reducedMotion) {
    gsap.fromTo(modalBackdrop, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.22 });
    gsap.fromTo(modal, { autoAlpha: 0, y: 24, scale: 0.97 }, { autoAlpha: 1, y: 0, scale: 1, duration: 0.42, ease: "power3.out" });
  }
  modal.querySelectorAll(".modal-close").forEach((button) => button.addEventListener("click", closeModal));
  modal.querySelectorAll(".modal-confirm").forEach((button) => button.addEventListener("click", () => {
    closeModal();
    showToast("操作已完成（演示环境）");
  }));
  modal.querySelector<HTMLElement>("button, input, select")?.focus();
}

function closeModal() {
  if (reducedMotion) {
    modalBackdrop.style.display = "none";
    modalBackdrop.setAttribute("aria-hidden", "true");
    return;
  }
  gsap.to(modalBackdrop, { autoAlpha: 0, duration: 0.18, onComplete: () => {
    modalBackdrop.style.display = "none";
    modalBackdrop.setAttribute("aria-hidden", "true");
  } });
}

function showToast(message: string) {
  const toast = document.createElement("div");
  toast.className = "toast";
  toast.textContent = message;
  toastStack.append(toast);
  if (!reducedMotion) gsap.fromTo(toast, { autoAlpha: 0, x: 24 }, { autoAlpha: 1, x: 0, duration: 0.35, ease: "power2.out" });
  window.setTimeout(() => {
    gsap.to(toast, { autoAlpha: 0, x: 18, duration: reducedMotion ? 0 : 0.25, onComplete: () => toast.remove() });
  }, 2800);
}

function setAssistant(open: boolean) {
  assistantTrigger.setAttribute("aria-expanded", String(open));
  if (open) {
    assistantPanel.style.display = "block";
    if (!reducedMotion) gsap.fromTo(assistantPanel, { autoAlpha: 0, y: 18, scale: 0.97, transformOrigin: "bottom right" }, { autoAlpha: 1, y: 0, scale: 1, duration: 0.42, ease: "power3.out" });
    document.querySelector<HTMLInputElement>("#assistant-query")?.focus();
  } else if (reducedMotion) {
    assistantPanel.style.display = "none";
  } else {
    gsap.to(assistantPanel, { autoAlpha: 0, y: 12, scale: 0.98, duration: 0.22, ease: "power2.in", onComplete: () => {
      assistantPanel.style.display = "none";
    } });
  }
}

function addAssistantMessage(text: string, user = false) {
  const container = document.querySelector<HTMLElement>("#assistant-messages");
  if (!container) return;
  const message = document.createElement("div");
  message.className = `message${user ? " user" : ""}`;
  message.textContent = text;
  container.append(message);
  container.scrollTop = container.scrollHeight;
  if (!reducedMotion) gsap.fromTo(message, { autoAlpha: 0, y: 8 }, { autoAlpha: 1, y: 0, duration: 0.3, ease: "power2.out" });
}

function assistantResponse(query: string) {
  if (query.includes("风险")) return "当前三项核心风险：① 历史欠税可能影响过户；② 拍卖前租赁真实性待核；③ 建设工程价款优先权尚未完全排除。建议在投委会前完成 7 项取证任务。";
  if (query.includes("买价") || query.includes("竞买")) return "3,135 万元来自综合谨慎估值 4,481 万元，再扣除目标利润、税费腾退成本 380 万元和融资成本 260 万元。对应安全边际约 30%，超过需二次审批。";
  if (query.includes("投委") || query.includes("摘要")) return "投委要点：拟收购第一顺位抵押债权，核心商业 9,012.6㎡；建议最高买价 3,135 万元，预计 IRR 21.4%；前置条件是取得欠税书面口径、排除工程价款优先权并核实租赁。";
  return "我已基于当前项目档案进行检索。演示版会展示结论、证据来源、可信度与复核状态；正式系统中，未经人工确认的专业结论不会直接生效。";
}

document.addEventListener("click", (event) => {
  const target = event.target as HTMLElement;
  const navTarget = target.closest<HTMLElement>("[data-nav]");
  if (navTarget?.dataset.nav) renderView(navTarget.dataset.nav as ViewId);
});

document.querySelector("#menu-button")?.addEventListener("click", () => sidebar.classList.toggle("open"));
document.querySelector("#search-button")?.addEventListener("click", () => setAssistant(true));
document.querySelector("#notice-button")?.addEventListener("click", () => showToast("您有 3 条高优先级待办，最近一项将于明日到期"));
assistantTrigger.addEventListener("click", () => setAssistant(assistantPanel.style.display !== "block"));
document.querySelector("#assistant-close")?.addEventListener("click", () => setAssistant(false));
document.querySelectorAll<HTMLButtonElement>(".suggestion").forEach((button) => {
  button.addEventListener("click", () => {
    const query = button.textContent?.trim() ?? "";
    addAssistantMessage(query, true);
    window.setTimeout(() => addAssistantMessage(assistantResponse(query)), 420);
  });
});
document.querySelector<HTMLFormElement>("#assistant-form")?.addEventListener("submit", (event) => {
  event.preventDefault();
  const input = document.querySelector<HTMLInputElement>("#assistant-query");
  const query = input?.value.trim() ?? "";
  if (!query) return;
  addAssistantMessage(query, true);
  if (input) input.value = "";
  window.setTimeout(() => addAssistantMessage(assistantResponse(query)), 460);
});
modalBackdrop.addEventListener("click", (event) => {
  if (event.target === modalBackdrop) closeModal();
});
document.addEventListener("keydown", (event) => {
  if (event.key === "Escape") {
    if (modalBackdrop.style.display === "grid") closeModal();
    else if (assistantPanel.style.display === "block") setAssistant(false);
    else sidebar.classList.remove("open");
  }
});

gsap.defaults({ duration: 0.55, ease: "power2.out" });
renderView("overview");
